import { hydrateRoot, createRoot } from 'react-dom/client';
import { loadableReady } from '@loadable/component';

// third party
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';

// project imports
import App from 'App';
import { BASE_PATH } from 'config';
import { createStore } from 'store/createStore';
import * as serviceWorker from 'serviceWorker';
import reportWebVitals from 'reportWebVitals';
import { ConfigProvider } from 'contexts/ConfigContext';
import { SSRDeviceProvider } from 'contexts/SSRDeviceContext';
import { HelmetProvider } from 'react-helmet-async';

// style + assets (variable WOFF2 via Fontsource; order before SCSS)
import '@fontsource-variable/montserrat';
import '@fontsource-variable/heebo';
import '@fontsource-variable/raleway';
import 'assets/scss/style.scss';

// ==============================|| GOOGLE ANALYTICS (gtag.js) ||============================== //

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

const initGoogleAnalytics = () => {
    if (!GA_MEASUREMENT_ID) {
        // GA ID not provided; skip initialization
        return;
    }

    // Avoid injecting the script multiple times
    if (document.querySelector(`script[data-ga-id="${GA_MEASUREMENT_ID}"]`)) {
        return;
    }

    // Load gtag.js
    const gaScript = document.createElement('script');
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    gaScript.setAttribute('data-ga-id', GA_MEASUREMENT_ID);
    document.head.appendChild(gaScript);

    // Initialize gtag
    const inlineScript = document.createElement('script');
    inlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${GA_MEASUREMENT_ID}');
    `;
    document.head.appendChild(inlineScript);
};

const scheduleGoogleAnalytics = () => {
    const run = () => {
        initGoogleAnalytics();
    };
    if (typeof window === 'undefined') return;
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 3500 });
    } else {
        window.addEventListener('load', run, { once: true });
    }
};

scheduleGoogleAnalytics();

// ==============================|| REACT DOM RENDER  ||============================== //

// Hydrate Redux state from server if available
// eslint-disable-next-line no-underscore-dangle
const preloadedState = window.__PRELOADED_STATE__ || {};
// Clean up the preloaded state from window
// eslint-disable-next-line no-underscore-dangle
delete window.__PRELOADED_STATE__;

// Create store with preloaded state for hydration
const store = createStore(preloadedState);

const container = document.getElementById('root');
const renderApp = (rootElement) => {
    if (container.hasChildNodes()) {
        hydrateRoot(container, rootElement);
    } else {
        const root = createRoot(container);
        root.render(rootElement);
    }
};

// Ensure all loadable() chunks used during SSR are ready before hydrating
loadableReady(() => {
    renderApp(
        <HelmetProvider>
            <Provider store={store}>
                <ConfigProvider>
                    <SSRDeviceProvider>
                        <BrowserRouter basename={BASE_PATH}>
                            <App />
                        </BrowserRouter>
                    </SSRDeviceProvider>
                </ConfigProvider>
            </Provider>
        </HelmetProvider>
    );
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
